export const meta = {
  id: '15-sizes-and-food',
  order: 15,
  titleFi: 'Koot ja ruoka',
  titleEn: 'Sizes and food',
  duolingoUnit: ['duo-16-sizes', 'duo-17-food'],
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'bōsa',
    'harrenka',
    'rōva',
    'kara',
    'byka',
    'mība',
    'kempa',
    'qumblie',
    'rōvēgrie-2',
    'ēngenka',
    'havon',
    'onjapos',
    '-illa',
    'avero',
    'averilla',
    'lōgor',
    'drōmon',
    'parklon',
    'jūlor',
    'lōtinty',
    'zāliapos',
    'kerilla',
    'prūbres',
    'bāngagon',
    'jesuragon',
    'ajesurilaros',
    'elilla',
    'kerio',
    'nollon',
    'parklor',
    'aerikagon',
    'hōzikagon',
    'keragon',
    'rholagon',
    'unektogon',
    'nōro',
    'ours:jūlrilla',
    'ours:gerpītsos',
    'ours:hōzikilla',
    'ours:jūlriapos',
    'ours:dōnilla',
    'ours:nāgēlōñe',
    'ours:haviapos',
    'ours:ūbrilliapos',
    'ours:jelio', 'ours:sylutella', 'ours:āzānnon', 'ours:vīgūljagon', 'ours:hisilla'],
  exampleIds: [
    'hotd-s3e03-013',
    'ex-1b0b3dd072a2',
    'got-s7e02-007',
    'ex-47d3871b0a68',
    'ex-3852e051b347',
    'ex-6a9b4ddeed12',
    'ex-e89b8e3ea14e',
    'ex-99a6889277ae',
    'ex-2f078fda07ce',
    'ex-0cb563e580b2',
    'ex-a4c88269a589',
    'ex-037339b6892d',
    'ex-a09eef5e87e3',
    'ex-a4e57bf76275',
    'ex-10ab94493dbb',
    'ex-9718a5728d97',
    'ex-5aa5c42abc08',
    'ex-bbf2e74d193f',
    'ex-c4ef146d4342',
    'ex-e87a36b41e2a',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Sizes',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Solar_class',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Adjectives',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Gender',
    'https://wiki.languageinvention.com/index.php?title=Harrenka',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Noun_Cases',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Food',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Gender',
    'https://wiki.languageinvention.com/index.php?title=-illa',
    'https://wiki.languageinvention.com/index.php?title=Ēngenka',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar#Reanalyzed_Collectives',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['adj-agreement'],
  grammarAlso: ['adj-position', 'paateopas'],
  showLines: ['hotd-s3e03-013', 'got-s7e02-007'],
  pronunciation: { focus: 'long-vowels', words: ['bōsa', 'rōva', 'mība', 'rōvēgrie', 'ēngenka'], lines: ['got-s7e02-007'] },
  phrases: ['rōvēgrie-2'],
  letter: {
    fi: 'Kerro, mitä söit viimeksi ja millaista se oli.',
    en: 'Write what you last ate and what it was like.',
  },
  scene: 'got-s7e02-1',
}

# Koot ja ruoka

Ensin koot: adjektiivit, joilla kuvataan kokoa, ja se, miten ne mukautuvat suvun ja luvun mukaan. Sitten ruoka: keittiön sanasto, aine- ja kappalesanojen ero ja johdin <Hv>-illa</Hv>.

## Koot

Tällä oppitunnilla opit joukon uusia adjektiiveja, joilla kuvataan kokoa ja mittaa. Duolingon Tips & Notes antaa vain yhden kieliopillisen neuvon: katso joka kerta tarkkaan, minkä suvun ja luvun substantiivia adjektiivi kuvaa. Siihen tämä osa keskittyy.

## Sanat

- <Hv glossed>rōva</Hv> ’iso, suuri’ ja <Hv glossed>byka</Hv> ’pieni’
- <Hv glossed>bōsa</Hv> ’pitkä’ ja <Hv glossed>mība</Hv> ’lyhyt; vähäinen, pieni’
- <Hv glossed>kempa</Hv> ’painava; vaikuttava’ ja <Hv glossed>qumblie</Hv> ’paksu’
- <Hv glossed>kara</Hv> ’suurenmoinen, loistava’
- <Hv glossed>harrenka</Hv> ’sopiva, sopivan kokoinen’
- <Hv glossed>Rōvēgrie!</Hv> ’Onneksi olkoon! Kippis! Mahtavaa!’

Duolingon ohjeet nostavat kaksi sanaa erikseen esiin, ja kummassakin suomesta on enemmän apua kuin englannista.

**<Hv>Bōsa</Hv>** tarkoittaa asiayhteydestä riippuen pitkää tai pitkäkasvuista. Englannissa nämä ovat eri sanoja (*long*, *tall*), mutta suomen *pitkä* kattaa kummankin aivan samoin. Pitkä käsivarsi ja pitkä ritari ovat kumpikin <Hv>bōsa</Hv>:

<Line id="hotd-s3e03-013" />

<Example id="ex-1b0b3dd072a2" />

Sana kuvaa myös ajan pituutta, kuten sarjan pitkässä yössä. <Hv glossed>Bantis</Hv> ’yö’ on aurinkosukua, joten adjektiivi on <Hv l="bōsa">bōsys</Hv>:

<Line id="got-s7e02-007" />

**<Hv>Harrenka</Hv>** on Duolingon ohjeiden mukaan sana, jolle englannissa ei ole suoraa vastinetta. Sanakirja antaa sille kolme merkitystä: ’sopiva, asianmukainen’, ’sopivan kokoinen tai pituinen’ ja ’odotusten mukainen’. Suomen *sopiva* ja *sopivan kokoinen* osuvat lähelle, joten tämäkin sana on suomalaiselle helpompi kuin englanninpuhujalle.

<Pronounce word="harrenka" />

<Example id="ex-47d3871b0a68" />

<Hv glossed>Verdon</Hv> ’määrä’ on maasukua, joten myös ’tämä’ (<Hv glossed>keson</Hv>) ja ’sopiva’ (<Hv glossed>harrenkon</Hv>) ovat maasuvun muodossa. Sana on tuttu kysymysten oppitunnilta: <Hv glossed>skorverdon</Hv> ’kuinka monta’.

## Neljä sukua – tai kaksi

Wikin adjektiivisivun mukaan adjektiivi mukautuu substantiiviinsa suvussa, luvussa ja sijassa. Sivu jakaa adjektiivit kolmeen luokkaan. <Term id="adjektiiviluokka">I-luokka</Term> erottaa kaikki neljä sukua. <Term id="adjektiiviluokka">II- ja III-luokka</Term> jakavat suvut kahteen ryhmään: kuu- ja aurinkosuvulla on yhteinen muoto, maa- ja vesisuvulla toinen.

Suurin osa tämän oppitunnin sanoista kuuluu <Term id="adjektiiviluokka">I-luokkaan</Term>:

<Paradigm lemma="rōva" filter="positive.pre" cases="nom,acc" numbers="sg,pl" />

<Hv glossed>Qumblie</Hv> ’paksu’ kuuluu <Term id="adjektiiviluokka">III-luokkaan</Term>, joten siinä on vain kaksi muotoryhmää:

<Paradigm lemma="qumblie" filter="positive.pre" cases="nom,acc" numbers="sg,pl" />

<Pronounce word="qumblie" />

<Example id="ex-3852e051b347" />

### Sija ja luku ovat tuttuja, suku on uusi

Suomessa adjektiivi mukautuu substantiiviin sijassa ja luvussa (*isoa johtajaa*, *isoissa taloissa*), joten tämä puoli on tuttu. Valyriassa mukaan tulee lisäksi suku, jota suomessa ei ole.

<Example id="ex-6a9b4ddeed12" />

<Interlinear hv="Rōv|on dēmal|ion rōv|i jent|i tēm|as." gloss="suuri-TERR.NOM valtaistuin-NOM suuri-SOL.ACC johtaja-ACC pistää-PRS.3SG" fi="Suuri valtaistuin pistää suurta johtajaa." />

Samassa lauseessa on sama adjektiivi kahdesti, eri muodoissa. Valtaistuin <Hv>dēmalion</Hv> on maasukua ja lauseen subjekti, joten adjektiivi on <Hv>rōvon</Hv>. Johtaja <Hv>jentys</Hv> on aurinkosukua ja lauseen objekti, joten adjektiivi on <Hv>rōvi</Hv>.

<Compare fi="iso|a" hv="rōv|i" label="objektin määrite: partitiivi ~ akkusatiivi" />

### Myös predikatiivi mukautuu

Adjektiivi mukautuu myös silloin, kun se on lauseen predikatiivi eli kertoo olla-lauseessa, millainen subjekti on. Kuusukuisen sanan kanssa se on <Hv l="byka">byka</Hv> tai <Hv>rōva</Hv>:

<Example id="ex-e89b8e3ea14e" />

<Example id="ex-99a6889277ae" />

Adjektiivi mukautuu samoin silloin, kun se kuvaa predikatiivina olevaa substantiivia: kuusukuinen <Hv>kempa</Hv> kuvaa sanaa <Hv>anne</Hv> ja aurinkosukuinen <Hv>karys</Hv> sanaa <Hv>jentys</Hv>.

<Example id="ex-2f078fda07ce" />

<Example id="ex-0cb563e580b2" />

Seuraavassa on kertausta ja uutta yhtä aikaa: maasuvun monikossa adjektiivin pääte on **-a**, sama kuin maasukuisen substantiivin monikossa.

<Example id="ex-a4c88269a589" />

<Interlinear hv="Zȳh|a byk|a issi." gloss="hänen.omansa-NOM.PL pieni-TERR.NOM.PL olla-PRS.3PL" fi="Hänen omansa ovat pieniä." />

<Hv>Zȳha</Hv> on omistussanojen oppitunnilta tutun pronominin <Hv>zȳhon</Hv> monikko. Wikin mukaan omistuspronomini taipuu kuin maasukuinen substantiivi, joten sen monikon pääte on -a, ja myös predikatiivi <Hv l="byka">byka</Hv> on maasuvun monikossa. Toisessa lauseessa <Hv l="pōjon">pōja</Hv> on samalla tavalla pronominin <Hv l="pōjon">pōjon</Hv> monikko.

<Note>
Huomaa suomen puolella sija. Monikollisen subjektin predikatiivi on suomessa yleensä partitiivissa (*ovat pieniä*). Valyriassa predikatiivi on wikin sijasivun mukaan nominatiivissa, ja se mukautuu subjektiinsa suvussa ja luvussa.
</Note>

## Etuasema ja jälkiasema

Valyriassa adjektiivi voi olla substantiivin edellä (etuasema) tai sen perässä (jälkiasema). Wikin adjektiivisivun mukaan jälkiasemassa adjektiivilla on täydet päätteet, etuasemassa lyhennetyt. Duolingon ohjeet näyttivät tämän jo aurinkosuvun oppitunnilla: <Hv glossed>ñuhyz jentyssy</Hv> mutta <Hv glossed>jentyssy ñuhyzy</Hv> ’minun johtajani’. Sama ero näkyy uusissakin sanoissa. Esimerkiksi aurinkosuvun monikossa on <Hv>bōsyz</Hv> substantiivin edellä mutta <Hv>bōsyzy</Hv> sen perässä:

<Paradigm lemma="bōsa" filter="positive" cases="nom" numbers="pl" />

Muista myös saman oppitunnin lisäsääntö aurinkosuvusta: etuaseman z muuttuu s:ksi, jos seuraava sana alkaa soinnittomalla konsonantilla, kuten k:lla, p:llä, s:llä, t:llä, h:lla tai q:lla (<Hv glossed>ñuhys tubissa</Hv> ’minun päiväni’).

## Rōvēgrie!

Lopuksi huudahdus. <Hv glossed>Rōvēgrie!</Hv> tarkoittaa ’onneksi olkoon’, ’kippis’ tai ’mahtavaa’. Sanakirjassa on myös samannäköinen adjektiivi <Hv glossed>rōvēgrie</Hv> ’valtava; erinomainen’.

<Pronounce l="rōvēgrie-2" word="rōvēgrie" />

<Practice hv="Rōva anne ēdrus." fi="Iso hevonen nukkuu." />

<Practice hv="Rōvon lenton urnen." fi="Näen ison talon." />

<Practice hv="Bykyz jaohossa ēdrusi." fi="Pienet koirat nukkuvat." />

<Practice hv="Ñuha muña bōsa issa." fi="Äitini on pitkä." />

## Kokojen kertaus

Kun opettelet uuden adjektiivin, kysy joka kerta neljä asiaa: minkä suvun substantiivia se kuvaa, onko substantiivi yksikössä vai monikossa, missä sijassa substantiivi on ja onko adjektiivi substantiivin edellä vai perässä. Kolme ensimmäistä määräytyvät substantiivista, ja niistä vain sija ja luku ovat suomesta tuttuja. Neljäs riippuu adjektiivin omasta paikasta. Puolet oppitunnista on takana – <Hv l="rōvēgrie-2">rōvēgrie!</Hv>

## Ruoka

Tulen ja jään laulun maailmassa ruoka on kaikkea muuta kuin sivuseikka: pidot, myrkyt, viini ja leipä kuljettavat tarinaa siinä missä miekat. Tällä oppitunnilla saat keittiön perussanaston – leipää, lihaa, munia, keittoa, viiniä – ja kaksi asiaa, joihin kannattaa pysähtyä: **aine- ja kappalesanojen** eron sekä pienen mutta yleisen johtimen <Hv>-illa</Hv>. Sillä muodostetut sanat näyttävät kuusukuisilta mutta käyttäytyvät kuin vesisukuiset.

Aloitetaan kysymyksellä, jonka voi esittää missä tahansa valyrialaisessa keittiössä:

<Example id="ex-037339b6892d" />

## Hyvä – mutta millä tavalla?

Tähän asti ’hyvä’ on ollut <Hv>sȳz</Hv>. Sitä voi käyttää ruoastakin, mutta silloin se kertoo **laadusta**: ruoka on huolella tehtyä, kunnollista. Kun haluat sanoa, että ruoka maistuu hyvältä, sana on <Hv glossed>ēngenka</Hv> ’maukas, hyvänmakuinen’.

Suomessakin tämän eron voi tehdä, mutta se ei ole pakollinen: ”hyvä leipä” voi tarkoittaa kumpaa tahansa, ja vasta ”maukas” tai ”hyvänmakuinen” rajaa merkityksen makuun. Valyriassa valinta tehdään joka kerta. Jos kehut kokin keittoa, kehut sen makua: <Hv>ēngenka</Hv>.

<Pronounce word="ēngenka" />

Huomaa paino. Pitkä <Hv>ē</Hv> sanan alussa ei vedä painoa puoleensa, vaan paino osuu toiseksi viimeiseen tavuun, koska se on raskas (tavu *gen* päättyy konsonanttiin). Suomalainen painottaisi luonnostaan ensimmäistä tavua, ja juuri siksi tämä sana kannattaa sanoa ääneen muutaman kerran.

## Ainesanat ja kappalesanat

Suomen kielioppi tuntee tämän eron hyvin. **Ainesana** tarkoittaa jotakin jakamatonta massaa (leipä, liha, maito), **kappalesana** jotakin, mitä voi laskea (porkkana, omena, muna). Suomessa ero näkyy ennen kaikkea partitiivissa: *syön leipää* mutta *syön porkkanan* tai *syön porkkanoita*. Ainesana on objektina yksikön partitiivissa, kappalesana joko kokonaisobjektina tai monikon partitiivissa.

Korkeavalyriassa ei ole partitiivia, eikä ero näy sijamuodossa. Se näkyy **suvussa**. Duolingon Tips & Notes kuvaa yleisen taipumuksen:

- kuu- ja aurinkosuvun sanat ovat yleensä kappalesanoja,
- maa- ja vesisuvun sanat ovat yleensä ainesanoja.

Siksi maasukuinen <Hv>havon</Hv> on suomeksi luontevimmin ”leipä, leipää” (ainetta), kun taas aurinkosukuinen <Hv>onjapos</Hv> on ”porkkana” – yksi kappale. Sama näkyy wikin kielioppisivulla: maasuvussa on paljon ruoka-aineita (<Hv>havon</Hv>, <Hv>parklon</Hv>), vesisuvussa nesteitä (tämän oppitunnin <Hv glossed>jūlor</Hv> ’maito’).

<Compare fi="leipä|ä" hv="hav|on" label="partitiivi ~ akkusatiivi: ainesana ilman omaa sijaa" />

Suomalaisen kannattaa siis muistaa yksi asia lisää: kun valyrialaisessa lauseessa puhutaan lihasta tai leivästä yksikössä, suomennos on usein partitiivissa, vaikka valyrian sana on aivan tavallisessa akkusatiivissa. Vertaa näitä kahta:

<Example id="ex-a09eef5e87e3" />

<Example id="ex-a4e57bf76275" />

Leijona syö lihaa (<Hv glossed>parklon</Hv>: yksikkö, ainetta), hevonen syö porkkanoita (<Hv glossed>onjapossa</Hv>: monikko, kappaleita). Suomennoksissa sama ero näkyy partitiivissa.

<Note>
Kyse on taipumuksesta, ei säännöstä. Maasukuinen <Hv glossed>drōmon</Hv> ’muna’ on mitä selvimmin laskettava – munia voi syödä viisikymmentä, tai ainakin yrittää:

<Example id="ex-10ab94493dbb" />

Kun sana on maa- tai vesisukua, kannattaa silti ensin kokeilla, toimiiko se suomeksi ainesanana.
</Note>

## -illa: a-loppuinen mutta vesisukua

<LessonLink n={9}>Oppitunnilla 9</LessonLink> näit jo <Hv>-illa</Hv>-loppuisia sanoja (<Hv>elilla</Hv>, <Hv>ārilla</Hv>, <Hv>averilla</Hv>). Nyt tulee itse johdin <Hv>-illa</Hv>. Sillä muodostetaan sanoja, jotka tarkoittavat kantasanaan liittyvää ainetta tai tulosta. Uusi sana <Hv glossed>avero</Hv> on ’viinirypäle’, ja siitä johdettu <Hv glossed>averilla</Hv> on ’viini’ – rypäleistä syntyvä aine.

Tässä on kielen pieni kummallisuus. Loppuvokaali <Hv>-a</Hv> on tyypillinen kuusuvun tuntomerkki, ja <Hv>averilla</Hv> **taipuukin** täsmälleen kuten <Hv>vala</Hv>. Adjektiivit kuitenkin **mukautuvat** siihen kuin se olisi vesisukua, siis kuten sanaan <Hv glossed>lōgor</Hv> ’laiva’. Nämä sanat tarkoittavat aineita, ja aineet ovat tavallisesti vesi- tai maasukua. Siksi <Hv>-illa</Hv>-sanat ovat vesisukua, vaikka eivät siltä näytä.

Taivutus noudattaa tuttua sanan <Hv>vala</Hv> kaavaa:

<Paradigm lemma="averilla" cases="nom,acc,gen" numbers="sg,pl" />

Adjektiivi sen sijaan on vesisuvun muodossa. Katso, miten <Hv>ēngenka</Hv> muuttuu suvun mukaan, kun se on substantiivin edellä:

<Paradigm lemma="ēngenka" filter="positive.pre" cases="nom,acc" numbers="sg" />

Duolingon ohjeissa on pari, jonka voit painaa mieleen sellaisenaan: <Hv glossed>Ēngenkor averilla</Hv> ’hyvä viini’ (nominatiivi) ja <Hv glossed>Ēngenkor averille</Hv> ’hyvää viiniä’ (akkusatiivi). Adjektiivi on kummassakin vesisuvun <Hv>ēngenkor</Hv>; vain substantiivin muoto vaihtuu, kuten <Hv>vala</Hv> → <Hv>vale</Hv>. Kuusukuisen sanan kanssa adjektiivi olisi edelleen <Hv>ēngenka</Hv>.

Oppitunnin alun kysymys toimii samoin:

<Interlinear hv="Ēngenk|or elill|e em|ā?" gloss="maukas-AQ.ACC hunaja-ACC omistaa-PRS.2SG" fi="Onko sinulla hyvää hunajaa?" />

<Note>
Wikin mukaan koko johdin on alun perin takaperoisjohdos sanasta <Hv glossed>elilla</Hv> ’hunaja’: sana tulkittiin uudelleen muodoksi ”mehiläinen + <Hv>-illa</Hv>”. Hunaja on siis koko sanaperheen esikuva. Samaan joukkoon kuuluvat tämän oppitunnin <Hv glossed>kerilla</Hv> ’keitto, pata’ ja värien oppitunnilla vastaan tuleva <Hv glossed>sȳndrilla</Hv> ’väri’.
</Note>

Suomi ei auta tässä lainkaan: meillä ei ole sukuja, joten ristiriita ”näyttää kuulta, käyttäytyy kuin vesi” on opeteltava ulkoa. Hyvä uutinen on, että <Hv>-illa</Hv> on helppo tunnistaa. Kun näet sen, ajattele heti: *aine, vesisuku*.

<Example id="ex-5aa5c42abc08" />

<Pronounce word="averilla" />

## Keittiön sanoja

Muu sanasto on suoraviivaista, mutta muutama asia kannattaa huomata.

Tämän oppitunnin **juomat** ovat vesisukua: <Hv glossed>jūlor</Hv> ’maito’ ja <Hv glossed>averilla</Hv> ’viini’. Ruoka-aineista moni on maasukua: <Hv glossed>havon</Hv> ’leipä’, <Hv glossed>parklon</Hv> ’liha’, <Hv glossed>nollon</Hv> ’sieni’. Oppitunnin kasvikset ja hedelmät taas kuuluvat kappalesanojen sukuihin: <Hv glossed>onjapos</Hv> ’porkkana’, <Hv glossed>zāliapos</Hv> ’sipuli’ ja <Hv glossed>prūbres</Hv> ’omena’ ovat aurinkosukua, <Hv glossed>nōro</Hv> ’selleri’ ja <Hv glossed>avero</Hv> ’viinirypäle’ kuusukua.

<Example id="ex-9718a5728d97" />

**Ateria**, <Hv l="parklor">parklor</Hv>, on oma tapauksensa. Se on syntynyt kollektiivista ja taipuu kuin kollektiivi. Siitä lisää myöhemmin, kun kollektiivi tulee vastaan.

**Ruoanlaiton verbejä** on tällä oppitunnilla runsaasti: <Hv glossed>keragon</Hv> ’kokata’, <Hv glossed>bāngagon</Hv> ’leipoa; valmistaa ateria’, <Hv glossed>hōzikagon</Hv> ’keittää, kiehua’, <Hv glossed>rholagon</Hv> ’sekoittaa yhteen’, <Hv glossed>aerikagon</Hv> ’liikuttaa, hämmentää’, <Hv glossed>jesuragon</Hv> ’ripotella jauhetta’ ja <Hv glossed>unektogon</Hv> ’leikata irti’. Huomaa myös, että <Hv glossed>keragon</Hv> ’kokata’, <Hv glossed>kerio</Hv> ’kokki’ ja <Hv glossed>kerilla</Hv> ’keitto’ alkavat samalla tavalla. Ne on helppo oppia rinnakkain.

<Example id="ex-bbf2e74d193f" />

<Example id="ex-c4ef146d4342" />

Verbin <Hv>jesuragon</Hv> kanssa ripoteltava aine on instrumentaalissa, sijassa, johon palaamme myöhemmin. Lause on silti mukava nähdä jo nyt, sillä siinä on sekä verbi että sen sukulainen <Hv glossed>ajesurilaros</Hv> ’mauste’:

<Example id="ex-e87a36b41e2a" />

Katso pitkät sanat tavuittain ja sano ne ääneen – paino ei osu ensimmäiseen tavuun:

<Pronounce word="ajesurilaros" />

<Pronounce word="lōtinty" />

<Pronounce word="hōzikagon" />

## Harjoituslauseita

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukoiden muodoilla. Ne eivät ole Petersonin lauseita.

<Note>
**Rakenna lause vaiheittain.** Damien Halmen (Academy of High Valyrian) tapa:

1. Merkitse suomenkieliseen lauseeseen subjekti, verbi ja objekti.
2. Kirjoita jokaisen sanan valyriankielinen perusmuoto sen alle.
3. Taivuta: objekti akkusatiiviin, adjektiivi sanansa sukuun, lukuun ja sijaan, etu- tai jälkiasemaan.
4. Pane verbi loppuun ja anna sille tekijän pääte.

Kokeile tällä: *Syön maukasta leipää.* Vertaa sitten ensimmäiseen harjoituslauseeseen.
</Note>

<Practice hv="Ēngenkon havon ipradan." fi="Syön maukasta leipää." />

<Practice hv="Ēngenkor kerille keran." fi="Teen maukasta keittoa." />

<Practice hv="Kerio averille mōzus." fi="Kokki juo viiniä." />

<Practice hv="Ābra havon bāngas." fi="Nainen leipoo leipää." />

Huomaa kaksi ensimmäistä lausetta: maasukuisen sanan <Hv>havon</Hv> kanssa adjektiivi on maasuvun muodossa <Hv>ēngenkon</Hv>, vesisukuisen sanan <Hv>kerilla</Hv> kanssa vesisuvun muodossa <Hv>ēngenkor</Hv>. Suomennoksissa kumpikin on ainesana ja siksi partitiivissa.

## Sanat

<Vocab />

<Check />
